import { useCallback } from 'react';

import type { InputBinding } from 'model/workflow';

export type BindingKind = InputBinding['kind'];

export interface UseInputBindingResult {
  /** The current discriminator. Defaults to `'Constant'` when binding is absent. */
  kind: BindingKind;

  /**
   * Switch the binding to a new kind. Preserves data from the previous
   * binding when the kind matches (so toggling away and back keeps the
   * user's input). Otherwise initialises with empty defaults.
   */
  setKind: (next: BindingKind) => void;

  /** Replace the entire `StepOutput` binding. */
  setStepOutputRef: (stepId: string, target: string) => void;

  /** Replace the entire `ProcessingJobInput` binding. */
  setProcessingJobInputTarget: (target: string) => void;

  /** Replace the entire `Expression` binding. */
  setExpression: (expression: string) => void;

  /** Replace the entire `Constant` binding. */
  setConstant: (value: unknown) => void;
}

const buildBindingForKind = (kind: BindingKind, previous?: InputBinding): InputBinding => {
  switch (kind) {
    case 'StepOutput': {
      const prev = previous && previous.kind === 'StepOutput' ? previous : null;
      return { kind: 'StepOutput', stepId: prev?.stepId ?? '', target: prev?.target ?? '' };
    }
    case 'ProcessingJobInput': {
      const prev = previous && previous.kind === 'ProcessingJobInput' ? previous : null;
      return { kind: 'ProcessingJobInput', target: prev?.target ?? '' };
    }
    case 'Expression': {
      const prev = previous && previous.kind === 'Expression' ? previous : null;
      return { kind: 'Expression', expression: prev?.expression ?? '' };
    }
    case 'Constant':
    default: {
      const prev = previous && previous.kind === 'Constant' ? previous : null;
      return { kind: 'Constant', value: prev?.value ?? '' };
    }
  }
};

/**
 * Hook that owns the kind-switching logic for an `InputBinding` slot.
 *
 * Wraps `onChange` so callers don't have to duplicate the per-variant
 * payload construction in every renderer. All setters are stable refs
 * (`useCallback` over `binding` + `onChange`) so they can safely be
 * passed to memoised children.
 */
export const useInputBinding = (
  binding: InputBinding | undefined,
  onChange: (next: InputBinding) => void,
): UseInputBindingResult => {
  const kind: BindingKind = binding?.kind ?? 'Constant';

  const setKind = useCallback(
    (next: BindingKind) => {
      onChange(buildBindingForKind(next, binding));
    },
    [binding, onChange],
  );

  const setStepOutputRef = useCallback(
    (stepId: string, target: string) => {
      onChange({ kind: 'StepOutput', stepId, target });
    },
    [onChange],
  );

  const setProcessingJobInputTarget = useCallback(
    (target: string) => {
      onChange({ kind: 'ProcessingJobInput', target });
    },
    [onChange],
  );

  const setExpression = useCallback(
    (expression: string) => {
      onChange({ kind: 'Expression', expression });
    },
    [onChange],
  );

  const setConstant = useCallback(
    (value: unknown) => {
      onChange({ kind: 'Constant', value });
    },
    [onChange],
  );

  return {
    kind,
    setKind,
    setStepOutputRef,
    setProcessingJobInputTarget,
    setExpression,
    setConstant,
  };
};
